import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const Contact = () => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        services: '',
        projectDetails: ''
    });

    const [status, setStatus] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openOffice, setOpenOffice] = useState('HQ Mumbai');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatus('sending');

        const serviceID = '';
        const templateID = '';
        const publicKey = '';

        const templateParams = {
            from_name: formData.fullName,
            from_email: formData.email,
            phone: formData.phone,
            company: formData.company,
            services: formData.services,
            project_details: formData.projectDetails
        };

        emailjs.send(serviceID, templateID, templateParams, publicKey)
            .then(() => {
                setStatus('success');
                setIsSubmitting(false);
                setFormData({ fullName: '', email: '', phone: '', company: '', services: '', projectDetails: '' });
                setTimeout(() => setStatus(''), 4000);
            })
            .catch(() => {
                setStatus('error');
                setIsSubmitting(false);
                setTimeout(() => setStatus(''), 4000);
            });
    };

    const indiaOffices = [
        { name: 'HQ Mumbai', address: 'Office No. 802, C Wing, 8th Floor, Trade Star Building, Sir M.V. Road, Andheri-Kurla Road, Andheri (East), Mumbai – 400059' },
        { name: 'Lucknow', address: 'B1/46, Sector G Jankipuram, Sector G, Jankipuram, Lucknow, Uttar Pradesh 226021' },
        { name: 'Hyderabad', address: 'Suite 12, 4th Floor, Cyber Gateway, Hitech City, Hyderabad – 500081' },
        { name: 'Bengaluru', address: '3rd Floor, Prestige Tech Park, Outer Ring Road, Marathahalli, Bengaluru – 560103' },
        { name: 'Pune', address: 'Office 5B, Cerebrum IT Park, Kalyani Nagar, Pune – 411014' },
        { name: 'Gurugram', address: '007, Ground Floor, MGF Megapolis Mall, MG Road, Gurugram – 122002' },
        { name: 'Kolkata', address: '4th Floor, PS Srijan Tech Park, Block GP, Sector V, Salt Lake, Kolkata – 700091' },
    ];

    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        borderRadius: '10px',
        border: '1.5px solid #e2e8f0',
        background: '#fff',
        fontSize: '0.95rem',
        color: '#0f172a',
        outline: 'none',
        transition: 'border-color 0.2s',
        fontFamily: 'inherit'
    };

    const labelStyle = {
        display: 'block',
        fontSize: '0.82rem',
        fontWeight: 600,
        color: '#475569',
        marginBottom: '8px',
        letterSpacing: '0.3px'
    };

    return (
        <>
            {/* ── HERO ── */}
            <section style={{
                position: 'relative', width: '100%', minHeight: '380px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover', backgroundPosition: 'center', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,16,40,0.80)' }} />
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.06) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '130px 15px 90px', textAlign: 'center' }}>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.4)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Contact Us
                            </span>
                        </div>
                        <h1 style={{
                            color: '#fff', fontWeight: 800,
                            fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
                            lineHeight: 1.15, margin: '0 auto 20px', maxWidth: '720px'
                        }}>
                            Let's Connect
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.72)', fontSize: '1.05rem',
                            lineHeight: 1.8, maxWidth: '520px', margin: '0 auto'
                        }}>
                            Whether you're exploring digital transformation, need technical support, or want to discuss a long-term IT strategy - our experts are ready to help.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ── CONTACT CARDS ── */}
            <section style={{ padding: '0', background: '#f8fafc' }}>
                <div className="container" style={{ transform: 'translateY(-40px)' }}>
                    <div className="row g-4">
                        {/* Email */}
                        <div className="col-lg-4 col-md-6">
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true }}
                                style={{
                                    background: '#fff', borderRadius: '16px',
                                    padding: '32px 28px', border: '1px solid #e2e8f0',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.07)', height: '100%'
                                }}
                            >
                                <div style={{
                                    width: '52px', height: '52px', borderRadius: '12px',
                                    background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
                                }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="white" strokeWidth="2" /><path d="M22 6l-10 7L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>Support</h3>
                                <p style={{ color: '#64748b', fontSize: '0.88rem', lineHeight: 1.7, marginBottom: '16px' }}>
                                    Get reliable technical assistance, service requests, and operational guidance from our certified support specialists.
                                </p>
                                <a href="mailto:response@solaris.co.in" style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    color: '#2563eb', fontWeight: 600, fontSize: '0.88rem', textDecoration: 'none'
                                }}>
                                    response@solaris.co.in
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </a>
                            </motion.div>
                        </div>

                        {/* Phone */}
                        <div className="col-lg-4 col-md-6">
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                viewport={{ once: true }}
                                style={{
                                    background: '#fff', borderRadius: '16px',
                                    padding: '32px 28px', border: '1px solid #e2e8f0',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.07)', height: '100%'
                                }}
                            >
                                <div style={{
                                    width: '52px', height: '52px', borderRadius: '12px',
                                    background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
                                }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.18 6.18l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>Phone Number</h3>
                                <p style={{ color: '#64748b', fontSize: '0.88rem', lineHeight: 1.7, marginBottom: '16px' }}>
                                    Speak directly with our team for urgent queries, project discussions, and service coordination.
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <a href="tel:+919717375675" style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        color: '#2563eb', fontWeight: 600, fontSize: '0.88rem', textDecoration: 'none'
                                    }}>
                                        +91 7571807772
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </a>
                                    <a href="tel:+915224955788" style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        color: '#2563eb', fontWeight: 600, fontSize: '0.88rem', textDecoration: 'none'
                                    }}>
                                        +91 52249 55788
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </a>
                                </div>
                            </motion.div>
                        </div>

                        {/* Address */}
                        <div className="col-lg-4 col-md-12">
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                                viewport={{ once: true }}
                                style={{
                                    background: '#fff', borderRadius: '16px',
                                    padding: '32px 28px', border: '1px solid #e2e8f0',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.07)', height: '100%'
                                }}
                            >
                                <div style={{
                                    width: '52px', height: '52px', borderRadius: '12px',
                                    background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
                                }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="white" strokeWidth="2" /><circle cx="12" cy="10" r="3" stroke="white" strokeWidth="2" />
                                    </svg>
                                </div>
                                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>Address</h3>
                                <p style={{ color: '#64748b', fontSize: '0.88rem', lineHeight: 1.7, marginBottom: '16px' }}>
                                    B1/46, Sector G Jankipuram, Sector G, Jankipuram, Lucknow, Uttar Pradesh 226021
                                </p>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    {[
                                        { icon: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z', href: '#' },
                                        { icon: 'M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z', href: '#' },
                                        { icon: 'M22.54 6.42a2.78 2.78 0 0 0-1.95-1.95C18.88 4 12 4 12 4s-6.88 0-8.59.47a2.78 2.78 0 0 0-1.95 1.95A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.5C5.12 20 12 20 12 20s6.88 0 8.59-.47a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z', href: '#' },
                                        { icon: 'M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z M4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z', href: '#' }
                                    ].map((s, i) => (
                                        <a key={i} href={s.href} style={{
                                            width: '34px', height: '34px', borderRadius: '8px',
                                            background: '#f1f5f9', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            color: '#475569', transition: 'all 0.2s', textDecoration: 'none'
                                        }}
                                            onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                            onMouseOut={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
                                        >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d={s.icon} />
                                            </svg>
                                        </a>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CONTACT FORM ── */}
            <section style={{ padding: '40px 0 90px', background: '#f8fafc' }}>
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        style={{
                            background: '#fff', borderRadius: '24px',
                            overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        <div className="row g-0">
                            {/* Left panel */}
                            <div className="col-lg-5" style={{
                                background: 'linear-gradient(145deg, #0f172a 0%, #1e3a5f 100%)',
                                padding: '56px 44px',
                                position: 'relative', overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute', top: '-60px', right: '-60px',
                                    width: '240px', height: '240px', borderRadius: '50%',
                                    background: 'rgba(37,99,235,0.15)', pointerEvents: 'none'
                                }} />
                                <div style={{
                                    position: 'absolute', bottom: '-40px', left: '-40px',
                                    width: '180px', height: '180px', borderRadius: '50%',
                                    background: 'rgba(14,165,233,0.1)', pointerEvents: 'none'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h2 style={{
                                        color: '#fff', fontWeight: 800,
                                        fontSize: 'clamp(1.8rem, 3vw, 2.4rem)',
                                        lineHeight: 1.2, marginBottom: '16px'
                                    }}>
                                        Send us a<br />message
                                    </h2>
                                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '40px' }}>
                                        Share your business needs, challenges, or queries. Our team will connect with you shortly.
                                    </p>

                                    {[
                                        {
                                            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.18 6.18l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                                            label: '+91 7571807772'
                                        },
                                        {
                                            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="white" strokeWidth="2" /><path d="M22 6l-10 7L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" /></svg>,
                                            label: 'response@solaris.co.in'
                                        },
                                        {
                                            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="white" strokeWidth="2" /><circle cx="12" cy="10" r="3" stroke="white" strokeWidth="2" /></svg>,
                                            label: 'Mumbai HQ, India'
                                        }
                                    ].map((item, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                                            <div style={{
                                                width: '40px', height: '40px', borderRadius: '10px',
                                                background: 'rgba(255,255,255,0.1)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                            }}>{item.icon}</div>
                                            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', fontWeight: 500 }}>{item.label}</span>
                                        </div>
                                    ))}

                                    <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 600 }}>
                                            Follow us on social media
                                        </p>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {['M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z', 'M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z', 'M22.54 6.42a2.78 2.78 0 0 0-1.95-1.95C18.88 4 12 4 12 4s-6.88 0-8.59.47a2.78 2.78 0 0 0-1.95 1.95A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.5C5.12 20 12 20 12 20s6.88 0 8.59-.47a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z', 'M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z M4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z'].map((d, i) => (
                                                <a key={i} href="#" style={{
                                                    width: '36px', height: '36px', borderRadius: '8px',
                                                    background: 'rgba(255,255,255,0.1)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: 'rgba(255,255,255,0.7)', textDecoration: 'none',
                                                    transition: 'background 0.2s'
                                                }}
                                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(37,99,235,0.5)'}
                                                    onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                                >
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d={d} />
                                                    </svg>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right: form */}
                            <div className="col-lg-7" style={{ padding: '56px 44px' }}>
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <label style={labelStyle}>Full Name *</label>
                                            <input
                                                type="text" name="fullName"
                                                value={formData.fullName} onChange={handleChange}
                                                placeholder="Your full name here"
                                                style={inputStyle} required
                                                onFocus={e => e.target.style.borderColor = '#2563eb'}
                                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label style={labelStyle}>Contact Number *</label>
                                            <input
                                                type="tel" name="phone"
                                                value={formData.phone} onChange={handleChange}
                                                placeholder="Your phone number here"
                                                style={inputStyle} required
                                                onFocus={e => e.target.style.borderColor = '#2563eb'}
                                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label style={labelStyle}>Email *</label>
                                            <input
                                                type="email" name="email"
                                                value={formData.email} onChange={handleChange}
                                                placeholder="your@email.com"
                                                style={inputStyle} required
                                                onFocus={e => e.target.style.borderColor = '#2563eb'}
                                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label style={labelStyle}>Company</label>
                                            <input
                                                type="text" name="company"
                                                value={formData.company} onChange={handleChange}
                                                placeholder="Enter company name"
                                                style={inputStyle}
                                                onFocus={e => e.target.style.borderColor = '#2563eb'}
                                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label style={labelStyle}>Service of Interest</label>
                                            <select
                                                name="services"
                                                value={formData.services} onChange={handleChange}
                                                style={{ ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', paddingRight: '40px' }}
                                                onFocus={e => e.target.style.borderColor = '#2563eb'}
                                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                            >
                                                <option value="">Select a service</option>
                                                <option value="Enterprise Networking">Enterprise Networking</option>
                                                <option value="Cyber Security">Cyber Security</option>
                                                <option value="Collaboration Services">Collaboration Services</option>
                                                <option value="Data Center">Data Center</option>
                                                <option value="Managed Services">Managed Services</option>
                                                <option value="Professional Services">Professional Services</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label style={labelStyle}>Leave us a message *</label>
                                            <textarea
                                                name="projectDetails"
                                                value={formData.projectDetails} onChange={handleChange}
                                                placeholder="Write your message here..."
                                                rows="5" required
                                                style={{ ...inputStyle, resize: 'vertical', minHeight: '120px' }}
                                                onFocus={e => e.target.style.borderColor = '#2563eb'}
                                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                            />
                                        </div>
                                    </div>

                                    {status === 'success' && (
                                        <div style={{
                                            marginTop: '16px', padding: '14px 18px', borderRadius: '10px',
                                            background: '#f0fdf4', border: '1px solid #bbf7d0',
                                            color: '#166534', fontSize: '0.9rem', fontWeight: 500
                                        }}>
                                            ✅ Message sent successfully! We'll get back to you soon.
                                        </div>
                                    )}
                                    {status === 'error' && (
                                        <div style={{
                                            marginTop: '16px', padding: '14px 18px', borderRadius: '10px',
                                            background: '#fef2f2', border: '1px solid #fecaca',
                                            color: '#991b1b', fontSize: '0.9rem', fontWeight: 500
                                        }}>
                                            ❌ Failed to send. Please try again or email us directly.
                                        </div>
                                    )}

                                    <div style={{ marginTop: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ display: 'flex' }}>
                                                {['https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=60&auto=format&fit=crop&q=80', 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&auto=format&fit=crop&q=80', 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&auto=format&fit=crop&q=80'].map((src, i) => (
                                                    <img key={i} src={src} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff', marginLeft: i > 0 ? '-10px' : '0' }} />
                                                ))}
                                            </div>
                                            <span style={{ color: '#64748b', fontSize: '0.82rem' }}>Trusted by 100+ clients across industries</span>
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '0',
                                                background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                                color: '#fff', border: 'none',
                                                padding: '14px 10px 14px 28px',
                                                borderRadius: '50px', fontWeight: 700, fontSize: '0.95rem',
                                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                                boxShadow: '0 6px 20px rgba(37,99,235,0.35)',
                                                transition: 'transform 0.2s, box-shadow 0.2s'
                                            }}
                                            onMouseOver={e => { if (!isSubmitting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(37,99,235,0.5)'; } }}
                                            onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.35)'; }}
                                        >
                                            <span style={{ marginRight: '14px' }}>{isSubmitting ? 'Sending...' : 'Submit Enquiry'}</span>
                                            <span style={{
                                                background: '#fff', borderRadius: '50%', width: '36px', height: '36px',
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                    <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </span>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ── GLOBAL OFFICES ── */}
            <section style={{ padding: '90px 0', background: '#0f172a', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(ellipse at 10% 50%, rgba(37,99,235,0.12) 0%, transparent 55%), radial-gradient(ellipse at 90% 20%, rgba(14,165,233,0.08) 0%, transparent 55%)',
                    pointerEvents: 'none'
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-start g-5">
                        {/* Left */}
                        <div className="col-lg-5">
                            <motion.div
                                initial={{ opacity: 0, x: -24 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                            >
                                <p style={{
                                    color: '#60a5fa', fontWeight: 700, letterSpacing: '3px',
                                    textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                                }}>OUR PRESENCE</p>
                                <h2 style={{
                                    color: '#fff', fontWeight: 800,
                                    fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)',
                                    lineHeight: 1.2, marginBottom: '20px'
                                }}>
                                    Our Offices<br />Across India
                                </h2>
                                <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '36px' }}>
                                    We support enterprises across India with reliable service quality and quick response from our offices in Mumbai, Lucknow, Hyderabad, Bengaluru, Pune, Gurugram, and Kolkata.
                                </p>

                                {/* Map placeholder */}
                                <div style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <iframe
                                        title="Solaris Mumbai HQ"
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.9!2d72.8777!3d19.1196!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDA3JzEwLjYiTiA3MsKwNTInMzkuNyJF!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                                        width="100%"
                                        height="240"
                                        style={{ border: 0, display: 'block', filter: 'grayscale(30%) invert(5%)' }}
                                        allowFullScreen=""
                                        loading="lazy"
                                    />
                                </div>
                            </motion.div>
                        </div>

                        {/* Right: location tabs + accordion */}
                        <div className="col-lg-7">
                            <motion.div
                                initial={{ opacity: 0, x: 24 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                viewport={{ once: true }}
                            >
                                {/* Accordion offices */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {indiaOffices.map((office, i) => (
                                        <motion.div
                                            key={office.name}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: i * 0.05 }}
                                            style={{
                                                borderRadius: '12px',
                                                border: openOffice === office.name ? '1px solid rgba(37,99,235,0.4)' : '1px solid rgba(255,255,255,0.08)',
                                                background: openOffice === office.name ? 'rgba(37,99,235,0.1)' : 'rgba(255,255,255,0.04)',
                                                overflow: 'hidden', transition: 'all 0.25s'
                                            }}
                                        >
                                            <button
                                                onClick={() => setOpenOffice(openOffice === office.name ? null : office.name)}
                                                style={{
                                                    width: '100%', textAlign: 'left',
                                                    background: 'none', border: 'none',
                                                    padding: '18px 22px',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    cursor: 'pointer', gap: '12px'
                                                }}
                                            >
                                                <span style={{
                                                    fontWeight: 700, fontSize: '0.98rem',
                                                    color: openOffice === office.name ? '#60a5fa' : 'rgba(255,255,255,0.8)'
                                                }}>{office.name}</span>
                                                <span style={{
                                                    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                                                    background: openOffice === office.name ? '#2563eb' : 'rgba(255,255,255,0.08)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#fff', fontSize: '1.1rem', fontWeight: 300, lineHeight: 1,
                                                    transition: 'all 0.25s'
                                                }}>
                                                    {openOffice === office.name ? '−' : '+'}
                                                </span>
                                            </button>
                                            {openOffice === office.name && (
                                                <div style={{ padding: '0 22px 20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
                                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="#60a5fa" strokeWidth="1.8" /><circle cx="12" cy="10" r="3" stroke="#60a5fa" strokeWidth="1.8" />
                                                        </svg>
                                                        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', lineHeight: 1.75, margin: 0 }}>
                                                            {office.address}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Contact;
